import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { useBrand } from '../context/BrandContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Slider } from '../components/ui/slider';
import useUsageLimits from '../hooks/useUsageLimits';
import UsageBadge from '../components/UsageBadge';
import PaywallModal from '../components/PaywallModal';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  Sparkles, 
  Loader2, 
  Download,
  Play,
  Pause,
  Volume2,
  Mic,
  Lock
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AudioGenerator() {
  const { currentBrand } = useBrand();
  const [text, setText] = useState('');
  const [voiceId, setVoiceId] = useState('21m00Tcm4TlvDq8ikWAM');
  const [stability, setStability] = useState([0.5]);
  const [similarityBoost, setSimilarityBoost] = useState([0.75]);
  const [voices, setVoices] = useState([]);
  const [generatedAudio, setGeneratedAudio] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingVoices, setLoadingVoices] = useState(true);
  const [isPlaying, setIsPlaying] = useState(false);
  const audioRef = useRef(null);

  const { limits, refresh: refreshLimits, isLocked } = useUsageLimits();
  const [showPaywall, setShowPaywall] = useState(false);
  const locked = isLocked('audio');

  useEffect(() => {
    fetchVoices();
  }, []);

  const fetchVoices = async () => {
    try {
      const response = await axios.get(`${API}/voices`, { withCredentials: true });
      setVoices(response.data);
      if (response.data.length > 0) {
        setVoiceId(response.data[0].voice_id);
      }
    } catch (error) {
      console.error('Failed to fetch voices:', error);
      toast.error('Failed to load voices');
    } finally {
      setLoadingVoices(false);
    }
  };

  const handleGenerate = async () => {
    if (!text.trim()) {
      toast.error('Please enter text to convert');
      return;
    }
    if (locked) {
      setShowPaywall(true);
      toast.error('Free limit reached. Contact support to upgrade.');
      return;
    }

    setLoading(true);
    setGeneratedAudio('');

    try {
      const response = await axios.post(
        `${API}/content/audio`,
        {
          text: text.trim(),
          voice_id: voiceId,
          stability: stability[0],
          similarity_boost: similarityBoost[0]
        },
        { withCredentials: true, timeout: 120000 }
      );
      setGeneratedAudio(response.data.audio_base64);
      toast.success('Audio generated successfully!');
      refreshLimits();
    } catch (error) {
      console.error('Audio generation error:', error);
      const detail = error.response?.data?.detail;
      if (error.response?.status === 403 && detail && typeof detail === 'object' && detail.code === 'FREE_TIER_LIMIT_REACHED') {
        setShowPaywall(true);
        toast.error('Free limit reached. Contact support to upgrade.');
        refreshLimits();
      } else {
        const msg = typeof detail === 'string' ? detail : (detail?.message || 'Failed to generate audio');
        toast.error(msg);
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePlayPause = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const handleDownload = () => {
    const link = document.createElement('a');
    link.href = `data:audio/mpeg;base64,${generatedAudio}`;
    link.download = `audio-${Date.now()}.mp3`;
    link.click();
    toast.success('Audio downloaded!');
  };

  const handleAudioEnded = () => {
    setIsPlaying(false);
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <h1 className="text-3xl font-bold text-zinc-100 font-['Manrope']">Audio Generator</h1>
        <p className="text-zinc-400 mt-1">Convert text to professional voice narration with ElevenLabs</p>
      </motion.div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Input Section */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.1 }}
          className="space-y-6"
        >
          <div className="p-6 rounded-xl bg-zinc-900/50 backdrop-blur-xl border border-zinc-800 space-y-6">
            {/* Voice Selector */}
            <div className="space-y-2">
              <Label className="text-zinc-300 flex items-center gap-2">
                <Mic className="w-4 h-4" />
                Voice
              </Label>
              {loadingVoices ? (
                <div className="h-12 bg-zinc-900/50 rounded-lg animate-pulse" />
              ) : (
                <Select value={voiceId} onValueChange={setVoiceId}>
                  <SelectTrigger 
                    data-testid="voice-select"
                    className="bg-zinc-900/50 border-zinc-800 h-12"
                  >
                    <SelectValue placeholder="Select a voice" />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-800 max-h-[300px]">
                    {voices.map((voice) => (
                      <SelectItem key={voice.voice_id} value={voice.voice_id}>
                        <div className="flex items-center gap-2">
                          <Volume2 className="w-4 h-4 text-zinc-500" />
                          <span>{voice.name}</span>
                          {voice.category && (
                            <span className="text-xs text-zinc-500">({voice.category})</span>
                          )}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>

            {/* Text Input */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-zinc-300">Text to Convert *</Label>
                <span className="text-xs text-zinc-500">{text.length} characters</span>
              </div>
              <Textarea
                data-testid="audio-text-input"
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder="Enter the text you want to convert to speech. This could be your script, narration, or any content you want voiced."
                className="bg-zinc-900/50 border-zinc-800 min-h-[200px] resize-none"
              />
            </div>

            {/* Voice Settings */}
            <div className="space-y-4">
              <h3 className="text-sm font-medium text-zinc-300">Voice Settings</h3>
              
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-sm text-zinc-400">Stability</Label>
                  <span className="text-xs text-zinc-500">{stability[0].toFixed(2)}</span>
                </div>
                <Slider
                  data-testid="stability-slider"
                  value={stability}
                  onValueChange={setStability}
                  min={0}
                  max={1}
                  step={0.05}
                  className="w-full"
                />
                <p className="text-xs text-zinc-600">Higher = more consistent, Lower = more expressive</p>
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-sm text-zinc-400">Similarity</Label>
                  <span className="text-xs text-zinc-500">{similarityBoost[0].toFixed(2)}</span>
                </div>
                <Slider
                  data-testid="similarity-slider"
                  value={similarityBoost}
                  onValueChange={setSimilarityBoost}
                  min={0}
                  max={1}
                  step={0.05}
                  className="w-full"
                />
                <p className="text-xs text-zinc-600">Higher = closer to original voice</p>
              </div>
            </div>

            {/* Generate Button */}
            <div className="space-y-2">
              <UsageBadge limits={limits} category="audio" onUpgradeClick={() => setShowPaywall(true)} />
              <Button
                data-testid="generate-audio-btn"
                onClick={handleGenerate}
                disabled={loading || !text.trim() || locked}
                title={locked ? 'Free limit reached. Upgrade to Pro to continue.' : ''}
                className="w-full h-12 rounded-xl font-medium transition-all duration-200 hover:-translate-y-0.5 disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:translate-y-0"
                style={ locked
                  ? { background: 'linear-gradient(135deg, #52525b 0%, #3f3f46 100%)' }
                  : { background: `linear-gradient(135deg, ${currentBrand.primary} 0%, ${currentBrand.secondary} 100%)` }
                }
              >
                {loading ? (
                  <span className="flex items-center gap-2">
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Generating...
                  </span>
                ) : locked ? (
                  <span className="flex items-center gap-2">
                    <Lock className="w-5 h-5" />
                    Audio limit reached — Upgrade
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <Sparkles className="w-5 h-5" />
                    Generate Audio
                  </span>
                )}
              </Button>
            </div>
          </div>
        </motion.div>

        {/* Output Section */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.2 }}
        >
          <div className="p-6 rounded-xl bg-zinc-900/50 backdrop-blur-xl border border-zinc-800 h-full flex flex-col">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-zinc-100 font-['Manrope']">Generated Audio</h2>
              {generatedAudio && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleDownload}
                  className="text-zinc-400 hover:text-zinc-200"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download
                </Button>
              )}
            </div>

            {loading ? (
              <div className="flex-1 flex items-center justify-center min-h-[300px]">
                <div className="text-center">
                  <Loader2 
                    className="w-12 h-12 animate-spin mx-auto mb-4"
                    style={{ color: currentBrand.primary }}
                  />
                  <p className="text-zinc-400">Generating audio...</p>
                  <p className="text-sm text-zinc-500 mt-1">This may take 15-30 seconds</p>
                </div>
              </div>
            ) : generatedAudio ? (
              <div className="flex-1 flex flex-col items-center justify-center min-h-[300px]">
                <audio
                  ref={audioRef}
                  src={`data:audio/mpeg;base64,${generatedAudio}`}
                  onEnded={handleAudioEnded}
                  className="hidden"
                />
                
                {/* Audio Player UI */}
                <div className="w-full max-w-md">
                  <div 
                    className="p-8 rounded-2xl border border-zinc-800"
                    style={{ background: `${currentBrand.primary}10` }}
                  >
                    {/* Visualizer Bars (Static) */}
                    <div className="flex items-end justify-center gap-1 h-24 mb-6">
                      {[...Array(20)].map((_, i) => (
                        <motion.div
                          key={i}
                          animate={isPlaying ? {
                            height: [20, Math.random() * 80 + 20, 20],
                          } : { height: 20 }}
                          transition={{
                            duration: 0.5,
                            repeat: isPlaying ? Infinity : 0,
                            repeatType: 'reverse',
                            delay: i * 0.05
                          }}
                          className="w-2 rounded-full"
                          style={{ background: currentBrand.primary, minHeight: 20 }}
                        />
                      ))}
                    </div>

                    {/* Play Button */}
                    <div className="flex justify-center">
                      <button
                        data-testid="play-pause-btn"
                        onClick={handlePlayPause}
                        className="w-16 h-16 rounded-full flex items-center justify-center transition-transform hover:scale-105"
                        style={{ background: currentBrand.primary }}
                      >
                        {isPlaying ? (
                          <Pause className="w-8 h-8 text-white" />
                        ) : (
                          <Play className="w-8 h-8 text-white ml-1" />
                        )}
                      </button>
                    </div>
                  </div>

                  <p className="text-center text-sm text-zinc-500 mt-4">
                    Click to {isPlaying ? 'pause' : 'play'} the generated audio
                  </p>
                </div>
              </div>
            ) : (
              <div className="flex-1 flex items-center justify-center border-2 border-dashed border-zinc-800 rounded-xl min-h-[300px]">
                <div className="text-center p-8">
                  <Volume2 className="w-12 h-12 text-zinc-700 mx-auto mb-4" />
                  <p className="text-zinc-500">Your generated audio will appear here</p>
                  <p className="text-sm text-zinc-600 mt-1">Fill in the form and click Generate</p>
                </div>
              </div>
            )}
          </div>
        </motion.div>
      </div>

      <PaywallModal
        open={showPaywall}
        onOpenChange={setShowPaywall}
        category="audio"
        limit={limits?.audio?.limit ?? 2}
      />
    </div>
  );
}
